import { getCloneId } from 'utils';
import { createCanvasWithGradient } from '../utils/GraphicsUtils';
import { FrameData, GradientFrameData } from './FlyerData';
import { Frame } from './Frame';
import { FrameType } from './FrameType';

const getRotatedScale = (
  width: number,
  height: number,
  rotation: number
): [number, number] => {
  const angle = (rotation * Math.PI) / 180;
  var dist = Math.sqrt(Math.pow(width, 2) + Math.pow(height, 2));
  var diagAngle = Math.asin(height / dist);

  var a1 = ((angle % (Math.PI * 2)) + Math.PI * 4) % (Math.PI * 2);
  if (a1 > Math.PI) {
    a1 -= Math.PI;
  }
  if (a1 > Math.PI / 2 && a1 <= Math.PI) {
    a1 = Math.PI / 2 - (a1 - Math.PI / 2);
  }

  var ang1 = Math.PI / 2 - diagAngle - Math.abs(a1);
  var ang2 = Math.abs(diagAngle - Math.abs(a1));

  var scale1 = (Math.cos(ang1) * dist) / height;
  var scale2 = (Math.cos(ang2) * dist) / width;

  return [scale1, scale2];
};

export class GradientFrame extends Frame {
  type = FrameType.Gradient;
  isEditable = true;

  private _colors: string[] = [];
  get colors(): string[] {
    return this._colors;
  }
  set colors(value: string[]) {
    this._colors = value;
    if (this.sprite) {
      this.updateTexture();
    }
    this.emit('update');
    this.generateThumbnail();
    this.emit('edited');
  }

  get boundingBox(): PIXI.Rectangle {
    return this._boundingBox;
  }
  set boundingBox(value: PIXI.Rectangle) {
    this._boundingBox = value;
    if (value) {
      this.updateTexture();
      if (this.sprite) {
        const [scale1, scale2] = getRotatedScale(
          value.width,
          value.height,
          this.rotation
        );
        const w = value.width * scale2;
        const h = value.height * scale1;
        this.sprite.x = value.x + value.width * 0.5;
        this.sprite.y = value.y + value.height * 0.5;
        this.sprite.width = w;
        this.sprite.height = h;
      }
    }
  }

  get rotation(): number {
    return this._rotation;
  }
  set rotation(value: number) {
    this._rotation = value;
    if (this.sprite) {
      this.sprite.angle = value;
      const [scale1, scale2] = getRotatedScale(
        this._boundingBox.width,
        this._boundingBox.height,
        this.rotation
      );
      const w = this._boundingBox.width * scale2;
      const h = this._boundingBox.height * scale1;
      this.sprite.width = w;
      this.sprite.height = h;
    }
  }

  async load() {
    if (this.isLoaded) {
      return;
    }
    this.isLoaded = true;
    this.generateThumbnail();
    this.emit('load');
  }

  updateTexture() {
    const oldTexture = this.sprite.texture;
    const canvas = createCanvasWithGradient(
      1,
      this.boundingBox.height,
      this.colors
    );
    this.sprite.texture = PIXI.Texture.from(canvas);
    oldTexture.destroy(true);
  }

  generateThumbnail(): void {
    if (this.thumbnailURL) {
      URL.revokeObjectURL(this.thumbnailURL);
    }
    const THUMBNAIL_SIZE: number = 120;
    const canvas = createCanvasWithGradient(
      THUMBNAIL_SIZE,
      THUMBNAIL_SIZE,
      this.colors
    );
    this.thumbnailURL = canvas.toDataURL('image/png');
  }

  populateWithData(data: FrameData) {
    const gradientData = data as GradientFrameData;
    this.boundingBox = new PIXI.Rectangle(
      0,
      0,
      this.stageWidth,
      this.stageHeight
    );
    this.colors = gradientData.colors;
    this.rotation = gradientData.rotation ?? 0;
  }

  asData(forDatabase: boolean): FrameData {
    const { colors } = this;
    const result: GradientFrameData = {
      ...super.asData(forDatabase),
      colors
    };
    return result;
  }

  dispose() {
    if (this.sprite) {
      this.sprite.destroy(true);
    }
  }

  clone(copyIndex: number): Frame {
    const clone = new GradientFrame(this.stageWidth, this.stageHeight);
    clone.id = getCloneId(this.id, copyIndex);
    clone.populateWithData(this.asData(false));
    return clone;
  }
}



// WEBPACK FOOTER //
// ./src/data/GradientFrame.ts